import type { MediaController as MediaControllerElement } from 'media-chrome'
import {
  MediaControlBar,
  MediaController,
  MediaFullscreenButton,
  MediaMuteButton,
  MediaPlayButton,
  MediaPosterImage,
  MediaSeekBackwardButton,
  MediaSeekForwardButton,
  MediaTimeDisplay,
  MediaTimeRange,
  MediaVolumeRange,
} from 'media-chrome/react'
import React from 'react'
import VimeoVideo from 'vimeo-video-element/react'
import YoutubeVideo from 'youtube-video-element/react'
import { pushDatalayer } from '../../utils/datalayer'
import { CookiePlaceholderLayout } from '../cookiebar/cookiebarLayout'
import { parseMediaVideoFile } from '../../utils/parseMedia'

export const Video = ({
  url,
  className,
  videoCookieAccepted,
  acceptVideoCookie,
  playerRef,
}: {
  url: string
  videoCookieAccepted: boolean
  acceptVideoCookie: () => void
  className?: string
  playerRef?: React.RefObject<MediaControllerElement>
}) => {
  const { provider, id } = parseMediaVideoFile(url)
  const controllerRef = playerRef || React.useRef<MediaControllerElement>(null)
  const language = document.documentElement.lang || 'en'

  React.useEffect(() => {
    const controller = controllerRef.current
    const handlePlay = () => pushDatalayer({ action: 'play', category: 'video', label: url })
    const handlePause = () => pushDatalayer({ action: 'pause', category: 'video', label: url })
    const handleEnded = () => pushDatalayer({ action: 'ended', category: 'video', label: url })

    if (controller && controller.media) {
      controller.media.addEventListener('play', handlePlay)
      controller.media.addEventListener('pause', handlePause)
      controller.media.addEventListener('ended', handleEnded)
    }
    return () => {
      const controller = controllerRef.current
      if (controller && controller.media) {
        controller.media.removeEventListener('play', handlePlay)
        controller.media.removeEventListener('pause', handlePause)
        controller.media.removeEventListener('ended', handleEnded)
      }
    }
  }, [url])

  return !videoCookieAccepted && provider != 'html5' ? (
    <CookiePlaceholderLayout onAccept={acceptVideoCookie} />
  ) : (
    <MediaController
      ref={controllerRef}
      className={className}
      lang={language}
      style={{
        width: '100%',
        aspectRatio: '16 / 9',
      }}
    >
      {provider == 'html5' ? (
        <video slot="media" src={url} preload="auto" />
      ) : provider == 'vimeo' ? (
        <VimeoVideo
          src={`https://player.vimeo.com/video/${id}`}
          slot="media"
          playsInline
          // https://help.vimeo.com/hc/en-us/articles/12426260232977-About-Player-parameters
          config={{ cc: true, keyboard: false }}
        />
      ) : provider == 'youtube' ? (
        <>
          <YoutubeVideo
            src={`https://www.youtube.com/embed/${id}`}
            slot="media"
            playsInline
            // https://developers.google.com/youtube/player_parameters#Parameters
            config={{
              cc_load_policy: 1,
              cc_lang_pref: language,
              disablekb: 1,
              hl: language,
              origin: window.location.origin,
              // YouTube refuses embeds without a Referer; production serves `Referrer-Policy: same-origin`
              referrerpolicy: 'strict-origin-when-cross-origin',
            }}
          />
          <MediaPosterImage slot="poster" src={`https://i.ytimg.com/vi/${id}/maxresdefault.jpg`} />
        </>
      ) : (
        <></>
      )}
      <div slot="centered-chrome">
        <MediaPlayButton />
      </div>
      <MediaControlBar className="bottom">
        <MediaSeekBackwardButton seekOffset={10} />
        <MediaPlayButton />
        <MediaSeekForwardButton seekOffset={10} />
        <MediaTimeRange />
        <MediaTimeDisplay showDuration />
        <MediaMuteButton />
        <MediaVolumeRange />
        <MediaFullscreenButton />
      </MediaControlBar>
    </MediaController>
  )
}
